import { OpenPanel } from '@openpanel/web';
import { useAnalyticsConsent } from '@/composables/useAnalyticsConsent';
import { isGpcEnabled } from '@/telemetry/gpc';

const clientId = import.meta.env.VITE_OPENPANEL_CLIENT_ID as string | undefined;
const apiUrl = import.meta.env.VITE_OPENPANEL_API_URL as string | undefined;

function createOpenPanel(): OpenPanel | null {
  if (!clientId) {
    console.debug('[openpanel] No client ID configured, analytics disabled');
    return null;
  }
  try {
    const instance = new OpenPanel({
      clientId,
      apiUrl: apiUrl || '/op',
      trackScreenViews: true,
      trackOutgoingLinks: true,
      disabled: true,
    });
    console.debug('[openpanel] Analytics initialized (awaiting consent)');
    return instance;
  } catch (err: unknown) {
    console.error('[openpanel] Failed to initialize SDK:', err);
    return null;
  }
}

export const op = createOpenPanel();

function enableAnalytics(): void {
  if (!op) return;
  if (isGpcEnabled()) {
    console.debug('[openpanel] GPC active — analytics suppressed despite consent');
    return;
  }
  try {
    op.ready();
    console.debug('[openpanel] Analytics enabled (consent granted)');
  } catch (err: unknown) {
    console.warn('[openpanel] ready() failed:', err);
  }
}

export const consent = useAnalyticsConsent({
  onAccept: enableAnalytics,
});

export function safeIdentify(payload: { profileId: string; firstName: string }): void {
  if (isGpcEnabled()) {
    console.debug('[openpanel] GPC active — identify suppressed');
    return;
  }
  if (consent.status.value !== 'accepted') return;
  try {
    op?.identify(payload)?.catch((err: unknown) => {
      console.warn('[openpanel] identify failed:', err);
    });
  } catch (err: unknown) {
    console.warn('[openpanel] identify threw:', err);
  }
}

// No GPC gate: clearing user identity is privacy-positive.
export function safeClear(): void {
  if (consent.status.value !== 'accepted') return;
  try {
    op?.clear();
  } catch (err: unknown) {
    console.warn('[openpanel] clear failed:', err);
  }
}
